import { useRef, useState, Children, cloneElement } from "react";
import Button, { ButtonProps } from "src/components/Button";
import Link from "src/components/Link";
import Loader from "src/components/Loader";
import useBlur from "src/hooks/useBlur";
import useElementSize from "src/hooks/useElementSize";

const positions = {
	"bottom-left": "left-0",
	"bottom-right": "right-0",
};

export type DropdownButtonPosition = keyof typeof positions;

interface DropdownButtonProps {
	children: any;
	menu?: {
		position?: DropdownButtonPosition;
	};
	button?: ButtonProps;
}

const DropdownButton = ({ children, menu, button }: DropdownButtonProps) => {
	const arrayChildren = Children.toArray(children);
	const [isVisible, setIsVisible] = useState(false);
	const ref = useRef() as any;
	useBlur(ref, () => setIsVisible(false));
	const buttonElement = useElementSize();

	return (
		<div {...{ ref }} className="flex flex-col relative">
			<div ref={buttonElement?.ref}>
				<Button
					onClick={(e) => {
						e.stopPropagation();
						setIsVisible(!isVisible);
					}}
					{...button}
				/>
			</div>

			{isVisible && (
				<div
					style={{ top: buttonElement?.size?.height + 4 }}
					className={`absolute z-[97] rounded-md min-w-md bg-white overflow-hidden shadow ${
						positions[menu?.position || "bottom-left"]
					}`}
				>
					{Children.map(arrayChildren, (child: any) =>
						cloneElement(child, {
							onClose: () => setIsVisible(false),
						})
					)}
				</div>
			)}
		</div>
	);
};

interface ItemProps {
	children?: any;
	icon?: string;
	to?: any;
	href?: string;
	onClick?: (close: () => void) => void;
	onClose?: () => void;
	loading?: boolean;
	className?: string;
	activeClassName?: string;
	locale?: string;
	disabled?: boolean;
	samePage?: boolean;
}

const Item = ({
	children,
	icon,
	to,
	onClick,
	onClose,
	loading,
	className,
	activeClassName,
	locale,
	disabled,
	samePage,
}: ItemProps) => {
	const Element = to ? Link : "div";
	const props: any = {};

	if (to) {
		props.to = to;
		props.activeClassName = activeClassName;
		props.samePage = samePage;
	}

	return (
		<Element
			{...props}
			{...{ locale, disabled }}
			onClick={(e) => {
				e.stopPropagation();
				if (disabled) {
					return;
				}
				if (onClick) {
					if (e) e?.preventDefault();
					onClick(() => onClose && onClose());
					return;
				}
				if (onClose) {
					onClose();
				}
			}}
			className={`cursor-pointer flex gap-3 items-center hover:bg-gray px-4 py-2 w-full  ${
				disabled && "opacity-40"
			} ${className || ""}`}
		>
			{icon && !loading && (
				<div className="w-4">
					<i aria-hidden="true" className={`${icon}`}></i>
				</div>
			)}
			{loading && (
				<div className="w-4 text-dark-400">
					<Loader />
				</div>
			)}
			<span className="whitespace-nowrap font-regular">{children}</span>
		</Element>
	);
};

Item.defaultProps = {
	variant: "gray-outline",
};

DropdownButton.Item = Item;

const Divider = () => {
	return (
		<div className="py-1">
			<hr className="border-gray" />
		</div>
	);
};

DropdownButton.Divider = Divider;

export default DropdownButton;
